import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { BgColorsOutlined, EditOutlined, SaveOutlined } from '@ant-design/icons';
import { equals, filter, prop, propOr, trim } from 'ramda';
import { Button } from 'antd';
import { propNotEq } from 'ramda-adjunct';
import { DoubleFont, Font } from '../../components/icons';
import MenuAdminLayout from '../../components/users/MenuAdminLayout';
import withUser from '../../withUser';
import InputTeamName from '../../components/InputTeamName';
import useCreateTeam from '../../hooks/useCreateTeam';
import useActionGuard from '../../hooks/useActionGuard';
import UpdateTeamNameMutation from '../../_graphql/mutations/teams/UpdateTeamNameMutation';
import { errorMessage, successMessage } from '../../utils/messageMutation';

const ElementsCard = ({ children, to, icon }) => {
  const history = useHistory();
  const className = classNames(`${to}-elements-card border border-solid rounded p-7 text-sm cursor-pointer flex justify-between uppercase font-semibold border-gray-200 bg-white`);
  return (
    <div
      className={className}
      onClick={() => history.push(`/equipe/${to}`)}
    >
      <div className="flex items-baseline gap-4">
        <div className="text-2xl text-flexi-text">
          {icon}
        </div>
        {children}
      </div>
      <img src="/images/chevron-left.svg" alt={`voir ${children}`} width="10px" />
    </div>
  );
};

const Elements = ({ userTeam, teams }) => {
  const [isEditingTeamName, setIsEditingTeamName] = useState(false);
  const { loading, run } = useActionGuard();
  const currentTeamName = prop('name')(userTeam);
  const otherTeams = filter(propNotEq(currentTeamName, 'name'), teams);
  const {
    touched,
    isFocus,
    teamName,
    setTeamName,
    alreadyExists,
    isValid,
    onBlur,
    onFocus,
    reset
  } = useCreateTeam(otherTeams, currentTeamName);

  const onToggleTeamNameEdition = () => {
    if (isEditingTeamName) {
      const newTeamName = trim(teamName);
      run(`update-team-name-${prop('teamId', userTeam)}`, (done) => UpdateTeamNameMutation({ name: newTeamName }, (ok, error) => {
        if (ok && !error) {
          reset(newTeamName);
          setIsEditingTeamName(false);
          successMessage('nom d\'équipe', 'modifié');
        } else {
          errorMessage();
        }
        done(ok, error);
      }));
    } else {
      setIsEditingTeamName(true);
    }
  };

  return (
    <MenuAdminLayout
      title="Paramétrer mon équipe"
      description="Gérez les styles, les couleurs ainsi que les différentes polices de votre équipe"
    >
      <p className="mt-2 mb-1">Nom actuel de l'équipe</p>
      <div className="flex items-start gap-2">
        {isEditingTeamName ? (
          <div className="w-56 shrink-0">
            <InputTeamName
              className="w-full"
              teamName={teamName}
              touched={touched}
              isFocus={isFocus}
              setTeamName={setTeamName}
              alreadyExists={alreadyExists}
              isValid={isValid}
              onBlur={onBlur}
              onFocus={onFocus}
            />
          </div>
        ) : <strong className="w-56">{currentTeamName}</strong>}
        <Button
          size="small"
          icon={isEditingTeamName ? <SaveOutlined /> : <EditOutlined />}
          onClick={onToggleTeamNameEdition}
          className={classNames('small-white-button self-start', { 'bg-white !border-flexi-border !text-flexi-text': !isEditingTeamName }, { '!bg-flexi-primary-color !text-white': isEditingTeamName })}
          disabled={isEditingTeamName ? !isValid || alreadyExists || equals(currentTeamName, teamName) : false}
          loading={propOr(false, `update-team-name-${prop('teamId', userTeam)}`, loading)}
        >{isEditingTeamName ? 'Enregistrer' : 'Modifier'}
        </Button>
      </div>
      <div className="grid md:grid-cols-3 xs:grid-cols-1 gap-6 mt-3">
        <ElementsCard to="styles" icon={<DoubleFont />}>
          Styles
        </ElementsCard>

        <ElementsCard to="couleurs" icon={<BgColorsOutlined />}>
          Couleurs
        </ElementsCard>

        <ElementsCard to="police" icon={<Font />}>
          Police
        </ElementsCard>
      </div>
    </MenuAdminLayout>
  );
};

export default withUser(Elements);
